// One purchase channel in the Shipping providers hub: status, stored key names,
// balance, a no-cost connection test, key entry and the on/off switch.
//
// Keys are write-only. The backend merges a save over the stored secrets, so a
// blank input keeps the saved value and rotating one field never erases another.
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PexIcon } from '../../../components/icons/PexIcon'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { API_BASE } from '../../../lib/api'
import { formatCurrency } from '../../../lib/currency'
import { formatDateTime } from '../../../lib/datetime'
import { maskProviderNames } from '../../../lib/shippingProviders'
import { toast } from '../../../lib/toast'
import { useT } from '../../../i18n'
import {
  shippingProvidersI18n,
  type ShippingProvidersStrings,
} from '../../../i18n/admin/shipping-providers'
import {
  CREDENTIALS_QUERY_KEY,
  type CarrierCredential,
  type ConnectionTest,
  type ProviderDef,
  type ProviderId,
} from './provider-hub.lib'

const base = `${API_BASE}/admin/carrier-credentials`

type CardStrings = ShippingProvidersStrings['card']

async function send(
  path: string,
  init: RequestInit,
  requestFailed: CardStrings['requestFailed'],
): Promise<unknown> {
  const res = await fetch(`${base}${path}`, {
    headers: { 'content-type': 'application/json' },
    ...init,
  })
  const body = await res.json().catch(() => null)
  if (!res.ok) {
    const detail = (body as { detail?: { message?: string } } | null)?.detail
    // The gateway names itself in its refusals; the operator reads our name.
    throw new Error(
      detail?.message ? maskProviderNames(detail.message) : requestFailed(res.status),
    )
  }
  return body
}

function StatusBadge({ row, t }: { row: CarrierCredential | undefined; t: CardStrings }) {
  if (!row) return <Badge variant="neutral" className="whitespace-nowrap">{t.notConnected}</Badge>
  if (!row.active) return <Badge variant="warning" className="whitespace-nowrap">{t.off}</Badge>
  return <Badge variant="success" className="whitespace-nowrap">{t.active}</Badge>
}

function ActiveSwitch({
  checked,
  pending,
  label,
  onChange,
}: {
  checked: boolean
  pending: boolean
  label: string
  onChange: (next: boolean) => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={pending}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)] disabled:opacity-60 ${
        checked ? 'bg-[var(--color-ink)]' : 'bg-[var(--color-border-strong)]'
      }`}
    >
      <span
        className={`absolute left-0 top-0.5 h-5 w-5 rounded-full bg-[var(--color-surface)] shadow transition-transform duration-200 ${
          checked ? 'translate-x-[22px]' : 'translate-x-[2px]'
        }`}
      />
    </button>
  )
}

function FieldChips({
  fields,
  stored,
  t,
}: {
  fields: string[]
  stored: string[]
  t: CardStrings
}) {
  return (
    <ul className="flex flex-wrap gap-1.5" aria-label={t.keys}>
      {fields.map((name) => {
        const saved = stored.includes(name)
        return (
          <li
            key={name}
            className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 font-mono text-[11px] ${
              saved
                ? 'border-[var(--color-border)] bg-[var(--color-surface-muted)] text-[var(--color-text)]'
                : 'border-dashed border-[var(--color-border-strong)] text-[var(--color-text-muted)]'
            }`}
          >
            <PexIcon name={saved ? 'lock' : 'key'} size="xs" />
            {name}
            <span className="sr-only">{saved ? t.saved : t.missingSr}</span>
          </li>
        )
      })}
    </ul>
  )
}

/** Label for the postage account behind a brokered balance, keyed by carrier code. */
const FUNDING_ACCOUNT: Record<string, string> = { stamps_com: 'Stamps.com' }

function Balance({
  provider,
  enabled,
  t,
}: {
  provider: ProviderId
  enabled: boolean
  t: CardStrings
}) {
  const q = useQuery({
    queryKey: ['carrier-balance', provider],
    enabled,
    retry: false,
    queryFn: async (): Promise<{ balance_usd: number; currency: string; funded_by?: string | null }> => {
      const res = await fetch(`${API_BASE}/admin/shipping/balance?provider=${provider}`)
      if (!res.ok) throw new Error(`${res.status}`)
      return res.json()
    },
  })
  // A failed re-read must not keep showing the last account's funds.
  const value = q.data && !q.isError ? formatCurrency(Number(q.data.balance_usd), q.data.currency) : '—'
  // Name the account only when the server read the balance from it.
  const account = q.data?.funded_by && !q.isError ? FUNDING_ACCOUNT[q.data.funded_by] : undefined
  const note = account && provider !== 'stamps' ? t.paidFrom(account) : undefined
  return (
    <span title={note}>
      {t.balance} <span className="tabular-nums text-[var(--color-text)]">{q.isLoading ? '…' : value}</span>
      {note && <span className="text-[var(--color-text-muted)]"> · {note}</span>}
    </span>
  )
}

export function ProviderCard({
  provider,
  row,
}: {
  provider: ProviderDef
  row: CarrierCredential | undefined
}) {
  const t = useT(shippingProvidersI18n).card
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [account, setAccount] = useState(row?.account_identifier ?? '')
  const [secrets, setSecrets] = useState<Record<string, string>>({})
  const [lastTest, setLastTest] = useState<ConnectionTest | null>(null)
  const storedFields = row?.credential_fields ?? []
  // A new key can point at another account, so its balance is re-read too.
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: CREDENTIALS_QUERY_KEY })
    void qc.resetQueries({ queryKey: ['carrier-balance', provider.id] })
  }

  const save = useMutation({
    mutationFn: () =>
      send(`/${provider.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          account_identifier: account.trim() || 'PEXIFY',
          credentials: Object.fromEntries(
            Object.entries(secrets).filter(([, value]) => value.trim() !== ''),
          ),
          active: row?.active ?? true,
        }),
      }, t.requestFailed),
    onSuccess: () => {
      setSecrets({})
      setEditing(false)
      toast.success(t.keysSaved(provider.label))
      refresh()
    },
    onError: (err) => toast.error(t.saveFailed(provider.label), { description: err.message }),
  })

  const toggle = useMutation({
    mutationFn: (next: boolean) =>
      next
        ? send(`/${provider.id}`, {
            method: 'PUT',
            body: JSON.stringify({
              account_identifier: row?.account_identifier ?? 'PEXIFY',
              credentials: {},
              active: true,
            }),
          }, t.requestFailed)
        : send(`/${provider.id}/deactivate`, { method: 'POST' }, t.requestFailed),
    onSuccess: (_data, next) => {
      toast.success(next ? t.isOn(provider.label) : t.isOff(provider.label))
      refresh()
    },
    onError: (err) => toast.error(t.switchFailed(provider.label), { description: err.message }),
  })

  const test = useMutation({
    mutationFn: async () => (await send(`/${provider.id}/test`, { method: 'POST' }, t.requestFailed)) as ConnectionTest,
    onSuccess: (result) => {
      setLastTest(result)
      if (result.ok) refresh()
    },
    onError: (err) => setLastTest({ ok: false, message: err.message, cheapest_usd: null }),
  })

  const connected = Boolean(row)
  const missing = provider.fields.filter((name) => !storedFields.includes(name))

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] text-[var(--color-text)]"
        >
          <PexIcon name="integration" size="md" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-[var(--color-text)]">{provider.label}</h3>
            <StatusBadge row={row} t={t} />
          </div>
          <p className="text-xs text-[var(--color-text-muted)]">{provider.role}</p>
        </div>
        {connected && (
          <ActiveSwitch
            checked={Boolean(row?.active)}
            pending={toggle.isPending}
            label={t.activeSwitch(provider.label)}
            onChange={(next) => toggle.mutate(next)}
          />
        )}
      </div>

      <FieldChips fields={provider.fields} stored={storedFields} t={t} />

      {editing && (
        <form
          className="space-y-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-3"
          onSubmit={(event) => {
            event.preventDefault()
            save.mutate()
          }}
        >
          <label className="block text-xs text-[var(--color-text-muted)]">
            {t.accountLabel}
            <input
              value={account}
              onChange={(event) => setAccount(event.target.value)}
              placeholder="PEXIFY"
              className="mt-1 w-full rounded-md border border-[var(--input-border)] bg-[var(--color-surface)] px-3 py-1.5 text-sm text-[var(--color-text)]"
            />
          </label>
          {provider.fields.map((name) => {
            const saved = storedFields.includes(name)
            return (
              <label key={name} className="block text-xs text-[var(--color-text-muted)]">
                <span className="font-mono">{name}</span>
                <input
                  type="password"
                  autoComplete="off"
                  value={secrets[name] ?? ''}
                  onChange={(event) => setSecrets((prev) => ({ ...prev, [name]: event.target.value }))}
                  placeholder={saved ? t.savedPlaceholder : t.pastePlaceholder}
                  className="mt-1 w-full rounded-md border border-[var(--input-border)] bg-[var(--color-surface)] px-3 py-1.5 font-mono text-[13px] text-[var(--color-text)]"
                />
              </label>
            )
          })}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" size="sm" variant="outline" onClick={() => setEditing(false)}>
              {t.cancel}
            </Button>
            <Button type="submit" size="sm" disabled={save.isPending}>
              {save.isPending ? t.saving : t.saveKeys}
            </Button>
          </div>
        </form>
      )}

      {lastTest && (
        <p
          role="status"
          className={`text-xs ${lastTest.ok ? 'text-[var(--color-success)]' : 'text-[var(--color-danger)]'}`}
        >
          {lastTest.ok ? t.connectionWorks : t.connectionFailed}
          {maskProviderNames(lastTest.message)}
          {lastTest.ok && lastTest.cheapest_usd !== null
            ? t.cheapest(formatCurrency(Number(lastTest.cheapest_usd), "USD"))
            : ''}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--color-border)] pt-3">
        <div className="flex flex-wrap gap-4 text-xs text-[var(--color-text-muted)]">
          {provider.hasBalance && (
            <Balance provider={provider.id} enabled={Boolean(row?.active)} t={t} />
          )}
          <span>
            {t.checked(row?.last_validated_at ? formatDateTime(row.last_validated_at) : t.never)}
          </span>
          {connected && missing.length > 0 && <span>{t.missing(missing.join(', '))}</span>}
        </div>
        {!editing && (
          <div className="flex gap-2">
            {connected ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!row?.active || test.isPending}
                  title={row?.active ? undefined : t.turnOnToTest}
                  onClick={() => test.mutate()}
                >
                  <PexIcon name="refresh" size="sm" /> {test.isPending ? t.testing : t.testConnection}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                  <PexIcon name="key" size="sm" /> {t.updateKeys}
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={() => setEditing(true)}>
                <PexIcon name="add" size="sm" /> {t.connect}
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  )
}
